<script setup lang="ts"></script>
<template>
    <ContentPage title="Declaração de Acessibilidade Fleequid">
        <section class="mb-8">
            <p class="mt-3 text-base">
                Fleequid (Adorea SRL) se compromete a tornar seu site acessível, conforme a lei de 9 de janeiro de 2004, n. 4. Esta declaração de
                acessibilidade se aplica a:
            </p>
            <p class="mt-2">
                <NuxtLink class="text-primary hover:underline" to="https://fleequid.com" target="_blank" external>https://fleequid.com</NuxtLink>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Acessibilidade</h2>
            <p class="mt-2">
                O nosso objetivo é assegurar que o conteúdo digital seja acessível a todos, incluindo pessoas com deficiência. Estamos empenhados em
                melhorar continuamente a usabilidade e em aplicar as melhores práticas de acessibilidade, garantindo a todos os utilizadores um acesso
                equitativo e inclusivo.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Declaração de Acessibilidade</h2>
            <p class="mt-2">
                Em conformidade com a legislação em vigor e em consonância com os nossos princípios de inclusão e igualdade de oportunidades, foi
                elaborada a presente Declaração de Acessibilidade. O documento detalha o estado de acessibilidade do nosso website, bem como as
                medidas implementadas para cumprir as Diretrizes de Acessibilidade para Conteúdos Web (WCAG). A declaração completa pode ser
                consultada e descarregada através do seguinte link:
            </p>
            <p class="mt-2">
                <a class="text-primary hover:underline" href="/docs/accessibility/accessibility-statement_PT.pdf" target="_blank">
                    Declaração de Acessibilidade Fleequid
                </a>
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Estado de conformidade</h2>
            <p class="mt-2">Parcialmente conforme</p>
            <p class="mt-3">
                Este site é parcialmente conforme aos requisitos previstos no apêndice A da norma UNI CEI EN 301549 em razão dos casos de não
                conformidade listados abaixo.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Conteúdos não acessíveis</h2>
            <p class="mt-3">Os conteúdos listados abaixo não são acessíveis por violação da lei de 9 de janeiro de 2004, n. 4.:</p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Para as páginas da web</h3>
            <h4 class="text-highlighted mt-3 font-medium">Em geral:</h4>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>1.3.1 Informações e correlações (Nível A)</li>
                <li>1.4.3 Contraste mínimo (Nível AA)</li>
                <li>1.4.4 Redimensionamento do texto (Nível AA)</li>
                <li>1.4.10 Recalculo do fluxo (apenas nível AA 2.1)</li>
                <li>2.4.1 Salto de blocos (Nível A)</li>
                <li>2.4.4 Propósito do link (no contexto) (Nível A)</li>
                <li>2.4.5 Diferentes modos (Nível AA)</li>
                <li>3.3.1 Identificação de erros (Nível A)</li>
                <li>4.1.2 Nome, papel, valor (Nível A)</li>
                <li>4.1.3 Mensagens de estado (Nível AA wcag 2.1)</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Elaboração da declaração de acessibilidade</h2>
            <p class="mt-3">Esta declaração foi elaborada em <time datetime="2025-08-20" class="font-bold">20 de agosto de 2025</time>.</p>
            <p class="mt-2">
                As informações contidas nesta declaração de acessibilidade foram derivadas de uma avaliação realizada por terceiros, mediante o uso de
                sistemas de validação automática e verificação direta, realizada manualmente pelo especialista técnico em acessibilidade.
            </p>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Ferramentas utilizadas para a validação automática:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Deque Axe Accessibility Checker (extensões do Firefox e Chrome)</li>
                <li>Siteimprove Accessibility Checker</li>
                <li>WCAG color contrast checker</li>
            </ul>

            <h3 class="text-highlighted mt-5 text-lg font-semibold">Tecnologias assistivas:</h3>
            <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Leitor de tela: NVDA</li>
            </ul>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Feedback e informações de contato</h2>
            <p class="mt-3">
                É possível enviar, a qualquer momento, relatórios diretamente para o endereço de email: <FleequidEmailLink type="accessibility" />
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Modo de envio de relatórios para a AgID</h2>
            <p class="mt-3">
                Em caso de resposta insatisfatória ou de falta de resposta, no prazo de trinta dias, à notificação ou ao pedido, o interessado pode
                enviar um relatório utilizando o modo indicado no site institucional da AgID.
            </p>
        </section>

        <section class="mb-10">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informações sobre o site</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Data de publicação do site:</dt>
                    <dd class="sm:col-span-2">
                        <time datetime="2024-08-27" class="font-bold">27 de agosto de 2024</time>
                    </dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Foram realizados testes de usabilidade:</dt>
                    <dd class="font-bold sm:col-span-2">não</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">CMS utilizado para o site:</dt>
                    <dd class="font-bold sm:col-span-2">Plataforma proprietária/custom desenvolvida internamente</dd>
                </div>
            </dl>
        </section>

        <section class="mb-4">
            <h2 class="text-highlighted text-xl font-semibold md:text-2xl">Informações sobre a estrutura</h2>
            <dl class="mt-4 space-y-4">
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Número de funcionários com deficiência presentes na administração:</dt>
                    <dd class="font-bold sm:col-span-2">1</dd>
                </div>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <dt class="font-medium">Número de postos de trabalho para funcionários com deficiência:</dt>
                    <dd class="font-bold sm:col-span-2">0</dd>
                </div>
            </dl>
        </section>
    </ContentPage>
</template>
